import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import type { AttributeTypeAdmin } from './types';
import { productAttributesEndpoint } from './use-get-attribute-dictionary-api';

export interface CreateAttributeTypePayload {
  key: string;
  label: string;
  isIdentityDefault?: boolean;
  sortOrder?: number;
  isActive?: boolean;
}

type TData = AttributeTypeAdmin;
type TError = AxiosError;
type TVariables = CreateAttributeTypePayload;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

// POST ums/v1/cms/master-product-attributes/types
export function useCreateAttributeTypeApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async (variables: TVariables): Promise<TData> => {
    try {
      // The API wraps the resource in an envelope: { data: {...} } — see
      // use-create-master-product-api.ts for the same fix.
      const response = await http.post(`${productAttributesEndpoint}/types`, variables);
      return response.data.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${productAttributesEndpoint}/types`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([`${productAttributesEndpoint}/types`]);
      queryClient.invalidateQueries([`${productAttributesEndpoint}/dictionary`]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
